import { useMemo } from "react";
import { Job, JobStatus, SheetData } from "../../../../pages/playground/types";
import { DEFAULT_GROUP_KEY, JOB_OBJECT_COLUMNS } from "../constants";

export interface UseJobGroupingReturn {
  groups: Record<string, Job[]> | null;
  jobs: Job[];
}

/**
 * Hook to filter and group jobs
 *
 * Filters jobs by:
 * - Status (if statusFilter is set)
 *
 * Groups jobs by:
 * - Selected column (if columnFilter is set)
 * - Special handling for garment_type column (comes from job object)
 * - Other columns come from sheet data
 *
 * @param jobs - Array of all jobs
 * @param statusFilter - Optional status filter
 * @param columnFilter - Optional column to group by
 * @param sheet - Sheet data for grouping
 * @returns Filtered jobs and optional groups
 */
export function useJobGrouping(
  jobs: Job[] | null,
  statusFilter: JobStatus | null,
  columnFilter: string | null,
  sheet: SheetData | null
): UseJobGroupingReturn {
  return useMemo(() => {
    if (!jobs) {
      return {
        groups: null,
        jobs: [],
      };
    }

    // First filter jobs based on status filter
    const filtered = jobs.filter((job) => {
      const matchesStatus = !statusFilter || job.status === statusFilter;
      return matchesStatus;
    });

    // If no column is selected, just return the filtered jobs
    if (!columnFilter || !sheet) {
      return {
        groups: null,
        jobs: filtered,
      };
    }

    // Group the jobs by the selected column
    const groups: Record<string, Job[]> = {};

    filtered.forEach((job) => {
      let groupKey = DEFAULT_GROUP_KEY;

      // Handle special columns that come directly from the job object
      if (columnFilter === JOB_OBJECT_COLUMNS.GARMENT_TYPE) {
        groupKey = job.garment_type || DEFAULT_GROUP_KEY;
      }
      // Handle sheet data columns
      else if (sheet && sheet.items.length > 0) {
        // Since the SKU is always the first field, use that for matching
        const sheetItems = sheet.items.filter((item) => {
          // Get the first property (which is the SKU field, whatever it's called)
          const skuField = Object.keys(item)[0];
          // String coercion: pandas parses numeric-only SKU columns as floats,
          // but job.master_id is always a string. Compare as strings to match.
          return String(item[skuField]).trim() === String(job.master_id).trim();
        });

        if (sheetItems && sheetItems.length > 0) {
          // Use the first sheet item's value for the selected column as the group key
          if (columnFilter in sheetItems[0]) {
            groupKey =
              sheetItems[0][columnFilter]?.toString() || DEFAULT_GROUP_KEY;
          } else {
            // Column not found in the item
            groupKey = DEFAULT_GROUP_KEY;
          }
        }
      }

      // Add job to the appropriate group
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(job);
    });

    return {
      groups,
      jobs: filtered,
    };
  }, [jobs, statusFilter, columnFilter, sheet]);
}
